<template>
	<div class="run-state">
		<RunSummary
			:running="running"
			:failed="failed"
			:title="summaryTitle"
			:subject-details="subjectDetails"
			:duration="duration"
			:completed-provider-count="completedProviderCount"
			:provider-count="providerProgress.length"
			:progress="progress"
			@cancel="emit('cancel')"
		/>
		<div class="providers">
			<ProviderProgressCard
				v-for="provider in providerProgress"
				:key="provider.provider.id"
				:progress="provider"
				:current-time="currentTime"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import ProviderProgressCard from "@/components/contact-research/ProviderProgressCard.vue"
import RunSummary from "@/components/contact-research/RunSummary.vue"
import {
	ContactResearchProviderId,
	type ContactResearchInput,
	type ContactResearchProviderProgress,
} from "@/types/contactResearch"

const props = defineProps<{
	input: ContactResearchInput
	providerProgress: ContactResearchProviderProgress[]
	running: boolean
	failed: boolean
	runStartedAt: string | null
	currentTime: number
}>()

const emit = defineEmits<{
	cancel: []
}>()

const { t } = useI18n()

const searchSubject = computed(() => {
	const isLinkedInOnlySearch =
		props.providerProgress.length === 1 &&
		props.providerProgress[0]?.provider.id === ContactResearchProviderId.Surfe &&
		Boolean(props.input.contactLinkedInUrl?.trim())

	return isLinkedInOnlySearch
		? props.input.contactLinkedInUrl?.trim() ?? ""
		: `${props.input.firstName} ${props.input.lastName}`.trim()
})

const subjectLocation = computed(() =>
	[props.input.contactMunicipality, props.input.contactProvince]
		.map(value => value?.trim())
		.filter(Boolean)
		.join(", ")
)

const subjectDetails = computed(() => {
	return [searchSubject.value, props.input.companyName?.trim(), subjectLocation.value].filter(Boolean).join(" · ")
})

const completedProviderCount = computed(() => props.providerProgress.filter(provider => provider.result).length)

const progress = computed(() => {
	if (props.providerProgress.length === 0) {
		return 0
	}

	return Math.round((completedProviderCount.value / props.providerProgress.length) * 100)
})

const duration = computed(() => {
	if (!props.runStartedAt) {
		return formatDuration(0)
	}

	return formatDuration(props.currentTime - new Date(props.runStartedAt).getTime())
})

const summaryTitle = computed(() => {
	if (props.running) {
		return t("contactResearchPage.run.searchingSources", { count: props.providerProgress.length })
	}

	const key = props.failed ? "failed" : "complete"

	return t(`contactResearchPage.run.${key}`, { name: searchSubject.value })
})

function formatDuration(durationMs: number): string {
	const seconds = Math.max(0, Math.floor(durationMs / 1000))
	const minutes = Math.floor(seconds / 60)
	const remainingSeconds = seconds % 60

	if (minutes > 0) {
		return `${minutes}m ${remainingSeconds}s`
	}

	return `${seconds}s`
}
</script>

<style scoped>
.providers {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	align-items: start;
	gap: var(--space-20);
}

@media (max-width: 1080px) {
	.providers {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

@media (max-width: 640px) {
	.providers {
		grid-template-columns: 1fr;
	}
}
</style>
